export type RuntimeConfig = {
  environment: string;
  authEnabled: boolean;
  apiBaseUrl: string;
  cognitoClientId: string;
  cognitoDomain: string;
};

export type PortalSession = {
  accessToken: string;
  idToken: string;
  expiresAt: number;
  email?: string;
};

const sessionKey = "airgap.portal.session";
const verifierKey = "airgap.portal.pkce.verifier";
const stateKey = "airgap.portal.pkce.state";

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

function randomValue(byteLength: number): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

function callbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

function decodeClaims(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  if (!payload) return {};
  const padded = payload
    .replaceAll("-", "+")
    .replaceAll("_", "/")
    .padEnd(Math.ceil(payload.length / 4) * 4, "=");
  try {
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) throw new Error("Portal configuration is unavailable.");
  return (await response.json()) as RuntimeConfig;
}

export function loadSession(): PortalSession | null {
  const stored = sessionStorage.getItem(sessionKey);
  if (!stored) return null;
  try {
    const session = JSON.parse(stored) as PortalSession;
    if (
      !session.accessToken ||
      !session.idToken ||
      session.expiresAt <= Date.now()
    ) {
      sessionStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch {
    sessionStorage.removeItem(sessionKey);
    return null;
  }
}

export async function beginLogin(config: RuntimeConfig): Promise<void> {
  const verifier = randomValue(64);
  const state = randomValue(32);
  sessionStorage.setItem(verifierKey, verifier);
  sessionStorage.setItem(stateKey, state);

  const parameters = new URLSearchParams({
    response_type: "code",
    client_id: config.cognitoClientId,
    redirect_uri: callbackUrl(),
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: await challenge(verifier),
    state,
  });
  window.location.assign(
    `https://${config.cognitoDomain}/oauth2/authorize?${parameters}`,
  );
}

export async function completeLogin(
  config: RuntimeConfig,
): Promise<PortalSession | null> {
  if (window.location.pathname !== "/auth/callback") return loadSession();
  const parameters = new URLSearchParams(window.location.search);
  const code = parameters.get("code");
  const returnedState = parameters.get("state");
  const expectedState = sessionStorage.getItem(stateKey);
  const verifier = sessionStorage.getItem(verifierKey);

  sessionStorage.removeItem(stateKey);
  sessionStorage.removeItem(verifierKey);
  if (!code || !returnedState || returnedState !== expectedState || !verifier) {
    throw new Error("The authentication response could not be verified.");
  }

  const response = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.cognitoClientId,
      redirect_uri: callbackUrl(),
      code_verifier: verifier,
      code,
    }),
  });
  if (!response.ok)
    throw new Error("The portal could not complete authentication.");
  const tokens = (await response.json()) as {
    access_token: string;
    id_token: string;
    expires_in: number;
  };
  const claims = decodeClaims(tokens.id_token);
  const session: PortalSession = {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    expiresAt: Date.now() + Math.max(60, tokens.expires_in - 30) * 1000,
    email: typeof claims.email === "string" ? claims.email : undefined,
  };
  sessionStorage.setItem(sessionKey, JSON.stringify(session));
  window.history.replaceState({}, document.title, "/");
  return session;
}

export function signOut(config: RuntimeConfig): void {
  sessionStorage.removeItem(sessionKey);
  const parameters = new URLSearchParams({
    client_id: config.cognitoClientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(
    `https://${config.cognitoDomain}/logout?${parameters}`,
  );
}

export async function loadPortalContext(
  config: RuntimeConfig,
  session: PortalSession,
) {
  const response = await fetch(`${config.apiBaseUrl}/v1/context`, {
    headers: { authorization: `Bearer ${session.accessToken}` },
  });
  if (!response.ok) throw new Error("Your portal membership is not available.");
  return (await response.json()) as {
    subject: string;
    tenantId: string;
    fixtureMode: boolean;
  };
}
